import {
  Mic, Video, MessageSquare, Users, HelpCircle, ShieldCheck, Hand, PhoneOff,
  MonitorUp, Circle, Sparkles, Send, MicOff, Search, Plus, Link2, Download,
  LayoutDashboard, Settings, ChevronRight, ChevronUp, Radio, Presentation,
  MoreHorizontal, Smile, Maximize2, PictureInPicture2, SlidersHorizontal,
  Signal, LogOut, DoorOpen, Megaphone, BarChart3, Lock, Pin, Upload, Check,
} from "lucide-react";
import { UdyogLogo } from "@/components/UdyogLogo";
import { MOCK, BANNERS } from "./tokens";

/* ------------------------------------------------------------------ *
 * Udyog Premium — single refined direction.
 * Obsidian + Emerald · Outfit display font · circular controls only.
 * ------------------------------------------------------------------ */

const Ambient = () => (
  <>
    <div className="pointer-events-none absolute -top-52 left-[12%] w-[680px] h-[680px] rounded-full bg-emerald-500/[0.10] blur-[150px]" />
    <div className="pointer-events-none absolute -bottom-64 right-[6%] w-[560px] h-[560px] rounded-full bg-emerald-400/[0.06] blur-[150px]" />
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.35]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
      }}
    />
  </>
);

const CountBadge = ({ value, testid }) => (
  <span
    data-testid={testid || "people-count-badge"}
    className="absolute -top-1.5 -right-2 min-w-[20px] h-[20px] px-1 rounded-full bg-emerald-400 text-black text-[10.5px] font-bold leading-none grid place-items-center ring-2 ring-[#0b0d0c] tabular-nums"
  >
    {value}
  </span>
);

const DockBtn = ({ icon: Icon, label, on, size = 48, big = false, testid, badge }) => (
  <button
    data-testid={testid || `dock-${label.toLowerCase()}`}
    className="group flex flex-col items-center gap-1.5 shrink-0"
  >
    <span className="relative">
      <span
        style={{ width: size, height: size }}
        className={`grid place-items-center rounded-full transition-[transform,background-color,box-shadow,border-color] duration-200 group-hover:-translate-y-[2px] ${
          big
            ? "bg-gradient-to-b from-emerald-400 to-emerald-600 text-black shadow-[0_10px_34px_-8px_rgba(16,185,129,0.85)] ring-1 ring-emerald-300/50"
            : on
            ? "bg-emerald-500/18 text-emerald-300 ring-1 ring-emerald-400/40"
            : "bg-white/[0.055] text-white/80 ring-1 ring-white/12 group-hover:bg-white/[0.10]"
        }`}
      >
        <Icon style={{ width: big ? 26 : 19, height: big ? 26 : 19 }} strokeWidth={1.7} />
      </span>
      {badge != null && <CountBadge value={badge} />}
    </span>
    <span
      className={`text-[9.5px] tracking-[0.16em] uppercase ${
        big ? "text-emerald-300/90 font-semibold" : "text-white/40 group-hover:text-white/70"
      }`}
    >
      {label}
    </span>
  </button>
);

function ChatPanel({ compact, role = "host", chatLocked = true }) {
  return (
    <div
      className="flex flex-col overflow-hidden rounded-[26px] bg-[#0b0d0c]/72 backdrop-blur-2xl ring-1 ring-white/[0.07] shadow-[0_24px_70px_-30px_rgba(0,0,0,0.95)]"
      style={compact ? undefined : { width: 348 }}
      data-testid="chat-panel"
    >
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-emerald-400" strokeWidth={1.8} />
          <span className="text-[12.5px] text-white/90">Chat</span>
        </div>
        {chatLocked && (
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] ring-1 ring-white/10 text-[9.5px] uppercase tracking-[0.16em] text-white/45">
            <Lock className="w-3 h-3" /> Read only
          </span>
        )}
      </div>

      <div className="px-3.5 pt-3.5">
        <div className="rounded-2xl bg-emerald-500/[0.08] ring-1 ring-emerald-400/25 p-3.5">
          <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.2em] text-emerald-300/90 mb-2">
            <Pin className="w-3 h-3" /> Pinned by host
          </div>
          <div className="text-[12px] text-white/90 leading-relaxed">
            Today’s notes PDF —{" "}
            <span className="text-emerald-300 underline decoration-emerald-400/40">udyogclasses.com/notes/p14</span>
          </div>
        </div>
      </div>

      <div className="flex-1 p-3.5 space-y-3.5 overflow-hidden">
        {MOCK.chat
          .filter((c) => c.me)
          .concat(MOCK.chat.filter((c) => c.me))
          .slice(0, 3)
          .map((c, i) => (
            <div key={i} className="flex gap-2.5">
              <span className="w-7 h-7 shrink-0 rounded-full bg-emerald-500/12 ring-1 ring-emerald-400/25 grid place-items-center text-[10px] text-emerald-300">
                N
              </span>
              <div className="max-w-[80%]">
                <div className="text-[9.5px] uppercase tracking-[0.18em] text-white/35 mb-1">Nikhil Sir · Host</div>
                <div className="px-3.5 py-2 text-[12px] rounded-2xl bg-white/[0.045] ring-1 ring-white/[0.07] text-white/90">
                  {i === 0
                    ? "Notes PDF sharing in 2 minutes."
                    : i === 1
                    ? "Solve Q4 and Q7 before the next class."
                    : "Doubt session link: udyogclasses.com/dc-batchb"}
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className="p-3 border-t border-white/[0.06]">
        {role === "host" ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 px-4 py-2.5 rounded-full bg-white/[0.04] ring-1 ring-white/[0.08] text-[12px] text-white/30">
              Message everyone…
            </div>
            <button className="w-10 h-10 shrink-0 grid place-items-center rounded-full bg-emerald-500 text-black shadow-[0_8px_26px_-8px_rgba(16,185,129,0.9)]">
              <Send className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.03] ring-1 ring-white/[0.06] text-[11.5px] text-white/40">
            <Lock className="w-3.5 h-3.5" /> Only the host can post here
          </div>
        )}
      </div>
    </div>
  );
}

const MORE_ITEMS = {
  host: [
    { icon: MonitorUp, label: "Share screen" },
    { icon: Presentation, label: "Whiteboard / slides" },
    { icon: Smile, label: "Reactions" },
    { icon: Maximize2, label: "Focus / full screen" },
    { icon: PictureInPicture2, label: "Picture-in-picture" },
    { icon: SlidersHorizontal, label: "Studio audio settings" },
    { icon: Signal, label: "Connection & quality" },
    { icon: Users, label: "Participants (184)" },
  ],
  student: [
    { icon: Maximize2, label: "Focus / full screen" },
    { icon: PictureInPicture2, label: "Picture-in-picture" },
    { icon: SlidersHorizontal, label: "Audio & video settings" },
    { icon: Signal, label: "Connection quality" },
  ],
};

const HOST_TOGGLES = [
  { icon: Circle, label: "Cloud recording", on: true },
  { icon: DoorOpen, label: "Waiting room", on: true },
  { icon: MicOff, label: "Mute everyone", on: true },
  { icon: Mic, label: "Students can unmute", on: false },
  { icon: MessageSquare, label: "Students can chat", on: false },
  { icon: Smile, label: "Reactions", on: true },
  { icon: HelpCircle, label: "Student questions", on: true },
  { icon: Lock, label: "Lock class (no new joins)", on: false },
];

function TogglePopover({ mobile }) {
  return (
    <div
      data-testid="host-toggles"
      className={`absolute z-50 bottom-full mb-4 ${mobile ? "right-0 w-64" : "right-0 w-80"} rounded-[24px] bg-[#0a0c0b] ring-1 ring-white/[0.12] shadow-[0_30px_80px_-20px_rgba(0,0,0,1)] p-2.5`}
    >
      <div className="px-3 py-2 text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80">Host controls</div>
      {HOST_TOGGLES.map((t) => (
        <button
          key={t.label}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[12.5px] text-white/80 hover:bg-white/[0.06] transition-colors duration-200"
        >
          <span className="w-8 h-8 shrink-0 grid place-items-center rounded-full bg-white/[0.05] ring-1 ring-white/10">
            <t.icon className="w-[15px] h-[15px]" strokeWidth={1.7} />
          </span>
          <span className="flex-1 text-left">{t.label}</span>
          <span
            className={`w-10 h-[22px] rounded-full relative shrink-0 transition-colors duration-200 ${
              t.on ? "bg-emerald-500" : "bg-white/[0.14] ring-1 ring-white/10"
            }`}
          >
            <span
              className="absolute top-[3px] w-4 h-4 rounded-full bg-black/85 transition-[left] duration-200"
              style={{ left: t.on ? 21 : 3 }}
            />
          </span>
        </button>
      ))}
    </div>
  );
}

function MorePopover({ items, title, mobile }) {
  return (
    <div
      data-testid="more-popover"
      className={`absolute z-50 bottom-full mb-4 ${mobile ? "right-0 w-56" : "right-0 w-72"} rounded-[24px] bg-[#0a0c0b] ring-1 ring-white/[0.12] shadow-[0_30px_80px_-20px_rgba(0,0,0,1)] p-2.5`}
    >
      <div className="px-3 py-2 text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80">{title}</div>
      {items.map((it) => (
        <button
          key={it.label}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[12.5px] text-white/75 hover:bg-white/[0.06] hover:text-white transition-colors duration-200"
        >
          <span className="w-8 h-8 shrink-0 grid place-items-center rounded-full bg-white/[0.05] ring-1 ring-white/10">
            <it.icon className="w-[15px] h-[15px]" strokeWidth={1.7} />
          </span>
          {it.label}
        </button>
      ))}
    </div>
  );
}

export function PremiumMeeting({ mobile = false, role = "host", mode = "camera", openMenu = "none" }) {
  const sharing = mode === "share";

  const Brand = (
    <div className="flex items-center gap-3.5 min-w-0">
      <UdyogLogo size={mobile ? 26 : 38} />
      <span className={`w-px bg-white/12 ${mobile ? "h-6" : "h-8"}`} />
      <div className="min-w-0">
        <div className={`font-display text-white tracking-tight truncate ${mobile ? "text-[13px]" : "text-[16px]"}`}>
          GS Foundation — Polity Ch. 4
        </div>
        {!mobile && (
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/35">Nikhil Sir · Live class</div>
        )}
      </div>
    </div>
  );

  const TopBar = (
    <div
      className={`relative z-30 flex items-center justify-between gap-3 ${
        mobile ? "px-4 pt-9 pb-3" : "px-7 py-4"
      }`}
    >
      {Brand}
      <div className="flex items-center gap-2.5 shrink-0">
        {role === "host" && (
          <button
            data-testid="people-count-pill"
            className="relative w-9 h-9 grid place-items-center rounded-full bg-white/[0.055] ring-1 ring-white/12 text-white/80 hover:bg-white/[0.10] transition-colors duration-200"
            title="Participants"
          >
            <Users className="w-[17px] h-[17px]" strokeWidth={1.8} />
            <CountBadge value={184} />
          </button>
        )}
        {role === "host" && (
          <span
            data-testid="rec-indicator"
            className="flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full bg-[#1a1c1b] text-[10.5px] tracking-[0.14em] text-white/70"
          >
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-70" />
              <span className="relative w-2 h-2 rounded-full bg-red-500" />
            </span>
            REC <span className="text-white/45 tabular-nums">41:20</span>
          </span>
        )}
        {!mobile && (
          <span className="px-2.5 py-1 rounded-full bg-white/[0.04] ring-1 ring-white/[0.08] text-[10.5px] tracking-[0.12em] text-white/45">
            HD · STABLE
          </span>
        )}
        {/* Leave — small solid red circle, tucked in the corner */}
        <button
          data-testid="leave-top-right"
          className="w-9 h-9 grid place-items-center rounded-full bg-red-600 text-white hover:bg-red-500 transition-colors duration-200"
          title="Leave class"
        >
          <PhoneOff className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );

  const Stage = (
    <div className="relative flex-1 min-h-0 rounded-[28px] overflow-hidden ring-1 ring-white/[0.08] bg-black shadow-[0_30px_90px_-40px_rgba(0,0,0,1)]">
      <img src={MOCK.teacher} alt="host" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-black/20" />
      <div className="absolute left-5 bottom-5 flex items-center gap-2">
        <span className="px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md text-[11.5px] text-white">
          Nikhil Sir
        </span>
        <span className="px-2.5 py-1.5 rounded-full bg-emerald-500/18 ring-1 ring-emerald-400/35 text-[9.5px] uppercase tracking-[0.16em] text-emerald-300">
          Speaking
        </span>
      </div>
    </div>
  );

  /* Screen share — edge to edge, nothing crops or letterboxes the content,
     and no other participant tiles are shown. Only the host's own tile. */
  const ShareStage = (
    <div className="relative flex-1 min-h-0 w-full bg-[#0e120f] overflow-hidden" data-testid="share-stage">
      <div
        className="absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
          backgroundSize: "42px 42px",
        }}
      />
      <div className={`absolute inset-0 flex flex-col justify-center ${mobile ? "p-5 gap-3" : "px-16 py-10 gap-6"}`}>
        <div className={`uppercase tracking-[0.3em] text-emerald-400/80 ${mobile ? "text-[8px]" : "text-[10px]"}`}>
          Shared screen · slide 14
        </div>
        <div className={`font-display text-white tracking-tight ${mobile ? "text-lg" : "text-6xl"}`}>
          Article 32 — Writ Jurisdiction
        </div>
        <div className={`h-px bg-emerald-500/50 ${mobile ? "w-20" : "w-48"}`} />
        <ul className={`text-white/70 ${mobile ? "space-y-1.5 text-[11px]" : "space-y-4 text-xl"}`}>
          <li>· Habeas Corpus — “to have the body”</li>
          <li>· Mandamus — command to a public authority</li>
          <li>· Prohibition, Certiorari, Quo Warranto</li>
        </ul>
      </div>
    </div>
  );

  const Strip = (
    <div className="flex gap-3">
      {MOCK.students.slice(0, mobile ? 3 : 4).map((s, i) => (
        <div
          key={i}
          className="relative flex-1 aspect-video rounded-2xl overflow-hidden ring-1 ring-white/[0.08] bg-neutral-900"
        >
          <img src={s} alt="student" className="w-full h-full object-cover" />
          {(i === 1 || i === 2) && (
            <span className="absolute top-2 right-2 w-5 h-5 grid place-items-center rounded-full bg-black/70">
              <MicOff className="w-3 h-3 text-red-400" />
            </span>
          )}
          <span className="absolute left-2 bottom-2 px-2 py-0.5 rounded-full bg-black/60 text-[9.5px] text-white/90">
            {["Aarav", "Priya", "Rohit", "Sneha"][i]}
          </span>
        </div>
      ))}
      {!mobile && (
        <div className="w-28 aspect-video rounded-2xl bg-white/[0.03] ring-1 ring-white/[0.08] grid place-items-center text-[11px] text-white/40">
          +180
        </div>
      )}
    </div>
  );

  const Dock = (
    <div
      data-testid="control-dock"
      className={`relative flex items-end ${mobile ? "gap-3.5 px-5" : "gap-6 px-8"} py-3 rounded-full bg-[#1a1d1b]`}
    >
      <div className={`flex items-end ${mobile ? "gap-3" : "gap-4"}`}>
        <DockBtn icon={Mic} label="Mic" on size={mobile ? 42 : 48} />
        <DockBtn icon={Video} label="Video" on size={mobile ? 42 : 48} />
      </div>
      <DockBtn icon={MessageSquare} label="Chat" big size={mobile ? 58 : 68} testid="dock-chat-primary" />
      <div className={`relative flex items-end ${mobile ? "gap-3" : "gap-4"}`}>
        {role === "host" ? (
          <DockBtn icon={ShieldCheck} label="Host" size={mobile ? 42 : 48} testid="dock-host-controls" />
        ) : (
          <DockBtn icon={Users} label="People" size={mobile ? 42 : 48} testid="dock-people" badge={184} />
        )}
        <DockBtn icon={MoreHorizontal} label="More" size={mobile ? 42 : 48} testid="dock-more" />
        {openMenu === "more" && (
          <MorePopover items={MORE_ITEMS[role]} title={`More · ${role === "host" ? "you" : "student"}`} mobile={mobile} />
        )}
        {openMenu === "host" && role === "host" && (
          <div className={mobile ? "absolute bottom-full right-14" : "absolute bottom-full right-16"}>
            <TogglePopover mobile={mobile} />
          </div>
        )}
      </div>
    </div>
  );

  if (mobile) {
    return (
      <div className="relative w-full h-full bg-[#070908] overflow-hidden flex flex-col">
        <Ambient />
        <div className="relative z-20 flex flex-col h-full">
          {sharing ? (
            <div className="px-4 pt-9 pb-2 flex items-center justify-between gap-2">
              {Brand}
              <div className="flex items-center gap-2">
                {role === "host" && (
                  <button className="relative w-8 h-8 grid place-items-center rounded-full bg-white/[0.055] ring-1 ring-white/12 text-white/80">
                    <Users className="w-4 h-4" strokeWidth={1.8} />
                    <CountBadge value={184} />
                  </button>
                )}
                <button className="w-8 h-8 grid place-items-center rounded-full bg-red-600 text-white">
                  <PhoneOff className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            </div>
          ) : (
            TopBar
          )}
          {sharing && ShareStage}
          {!sharing && (
            <>
              <div className="px-4 flex-1 flex min-h-0">{Stage}</div>
              <div className="px-4 pt-3">{Strip}</div>
              <button className="mx-4 mt-3 flex items-center justify-between px-4 py-2.5 rounded-2xl bg-white/[0.04] ring-1 ring-white/[0.08]">
                <span className="text-[12px] text-white/80">Chat</span>
                <span className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/18 text-emerald-300">12 new</span>
                  <ChevronUp className="w-4 h-4 text-white/40" />
                </span>
              </button>
            </>
          )}
          <div className={`flex justify-center ${sharing ? "py-2" : "py-4"}`}>{Dock}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-[#070908] overflow-hidden">
      <Ambient />
      <div className="relative z-20 h-full flex flex-col">
        {sharing ? (
          <div className="px-7 py-2.5 flex items-center justify-between">
            {Brand}
            <div className="flex items-center gap-2.5">
              <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/12 ring-1 ring-emerald-400/30 text-[10.5px] uppercase tracking-[0.16em] text-emerald-300">
                <Presentation className="w-3.5 h-3.5" /> You are presenting
              </span>
              {role === "host" && (
                <span className="flex items-center gap-2 pl-2.5 pr-3 py-1.5 rounded-full bg-[#1a1c1b] text-[10.5px] tracking-[0.14em] text-white/70">
                  <span className="relative flex w-2 h-2">
                    <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-70" />
                    <span className="relative w-2 h-2 rounded-full bg-red-500" />
                  </span>
                  REC <span className="text-white/45 tabular-nums">41:20</span>
                </span>
              )}
              {role === "host" && (
                <button
                  data-testid="people-count-pill"
                  className="relative w-9 h-9 grid place-items-center rounded-full bg-white/[0.055] ring-1 ring-white/12 text-white/80 hover:bg-white/[0.10] transition-colors duration-200"
                >
                  <Users className="w-[17px] h-[17px]" strokeWidth={1.8} />
                  <CountBadge value={184} />
                </button>
              )}
              <button
                data-testid="leave-top-right"
                className="w-9 h-9 grid place-items-center rounded-full bg-red-600 text-white hover:bg-red-500 transition-colors duration-200"
                title="Leave class"
              >
                <PhoneOff className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        ) : (
          TopBar
        )}

        {sharing ? (
          ShareStage
        ) : (
          <div className="flex-1 min-h-0 flex gap-5 px-7 pb-2">
            <div className="flex-1 min-w-0 flex flex-col gap-4">
              {Stage}
              {Strip}
            </div>
            <ChatPanel role={role} />
          </div>
        )}

        <div className={`flex justify-center ${sharing ? "py-2.5" : "py-5"}`}>{Dock}</div>
      </div>
    </div>
  );
}

export function PremiumJoin({ mobile = false, bannerId = "brand" }) {
  const banner = BANNERS.find((b) => b.id === bannerId) || BANNERS[0];

  const Card = (
    <div
      className={`rounded-[30px] overflow-hidden bg-[#0b0d0c]/75 backdrop-blur-2xl ring-1 ring-white/[0.08] shadow-[0_34px_90px_-40px_rgba(0,0,0,1)] ${
        mobile ? "w-full" : "w-[480px]"
      }`}
      data-testid="join-card"
    >
      <div className={`relative ${mobile ? "h-24" : "h-32"}`}>
        <img src={banner.url} alt="class banner" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d0c] via-[#0b0d0c]/35 to-transparent" />
        <div className="absolute left-6 bottom-4">
          <UdyogLogo size={mobile ? 22 : 28} />
        </div>
      </div>

      <div className={`${mobile ? "p-5" : "p-7"} space-y-6`}>
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.26em] text-emerald-400/80 mb-2">Live class</div>
          <div className={`font-display text-white leading-snug tracking-tight ${mobile ? "text-xl" : "text-2xl"}`}>
            GS Foundation — Polity Ch. 4
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.2em] text-white/35 mb-2">Your name</div>
            <div className="px-4 py-3.5 rounded-full bg-white/[0.04] ring-1 ring-white/[0.09] text-[13px] text-white/85">
              Aarav Kumar
            </div>
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.2em] text-white/35 mb-2">WhatsApp number</div>
            <div className="px-4 py-3.5 rounded-full bg-white/[0.04] ring-1 ring-white/[0.09] text-[13px] text-white/85">
              +91 98765 43210
            </div>
          </div>
        </div>
        <button className="w-full py-4 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 text-black font-semibold text-[12.5px] uppercase tracking-[0.2em] shadow-[0_14px_40px_-12px_rgba(16,185,129,0.9)]">
          Join class
        </button>
        <div className="flex items-center justify-center gap-5 text-[11px] text-white/40">
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> No app needed
          </span>
          <span className="flex items-center gap-1.5">
            <MicOff className="w-3.5 h-3.5 text-emerald-500" /> You join muted, camera off
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative w-full h-full bg-[#070908] overflow-hidden">
      <Ambient />
      <div className={`relative z-10 h-full flex flex-col items-center ${mobile ? "pt-12 px-5" : "pt-16"}`}>
        {Card}
      </div>
    </div>
  );
}

export function PremiumAdmin({ mobile = false, bannerId = "brand", onBanner = () => {} }) {
  const pill = (s) =>
    s === "LIVE"
      ? "bg-emerald-500/15 ring-1 ring-emerald-400/35 text-emerald-300"
      : s === "SCHEDULED"
      ? "bg-amber-400/10 ring-1 ring-amber-400/30 text-amber-300"
      : "bg-white/[0.04] ring-1 ring-white/[0.09] text-white/45";

  const Stats = (
    <div className={`grid ${mobile ? "grid-cols-2 gap-3" : "grid-cols-4 gap-5"}`}>
      {MOCK.stats.map((s) => (
        <div
          key={s.label}
          className="rounded-[24px] bg-white/[0.028] ring-1 ring-white/[0.07] p-5 hover:bg-white/[0.05] transition-colors duration-200"
        >
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/35 mb-2.5">{s.label}</div>
          <div className={`font-display text-white tracking-tight ${mobile ? "text-2xl" : "text-3xl"}`}>{s.value}</div>
        </div>
      ))}
    </div>
  );

  const Rows = (
    <div className="space-y-3">
      {!mobile && (
        <div className="grid grid-cols-12 px-5 text-[9.5px] uppercase tracking-[0.22em] text-white/30">
          <div className="col-span-5">Class</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Live / total</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
      )}
      {MOCK.meetings.map((m) =>
        mobile ? (
          <div key={m.room} className="rounded-[22px] bg-white/[0.03] ring-1 ring-white/[0.07] p-4 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-white/90 truncate">{m.title}</div>
              <div className="text-[11px] text-white/35 truncate">udyogclasses.com/{m.room.slice(-8)}</div>
              <div className="mt-2 flex items-center gap-2">
                <span className={`px-2.5 py-0.5 rounded-full text-[9.5px] tracking-[0.14em] ${pill(m.status)}`}>
                  {m.status}
                </span>
                <span className="text-[11px] text-white/40">
                  {m.active} / {m.total}
                </span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-white/30" />
          </div>
        ) : (
          <div
            key={m.room}
            className="grid grid-cols-12 items-center px-5 py-4 rounded-[22px] bg-white/[0.028] ring-1 ring-white/[0.06] hover:bg-white/[0.055] transition-colors duration-200"
          >
            <div className="col-span-5 min-w-0">
              <div className="text-[13.5px] text-white/90 truncate">{m.title}</div>
              <div className="text-[11px] text-white/35 truncate">udyogclasses.com/{m.room.slice(-8)}</div>
            </div>
            <div className="col-span-2">
              <span className={`px-2.5 py-1 rounded-full text-[9.5px] tracking-[0.14em] ${pill(m.status)}`}>
                {m.status}
              </span>
            </div>
            <div className="col-span-2 text-[13px] text-white/85">
              {m.active} <span className="text-white/35">/ {m.total}</span>
            </div>
            <div className="col-span-3 flex items-center justify-end gap-2">
              <button className="px-3 py-1.5 rounded-full text-[11px] flex items-center gap-1.5 text-white/65 ring-1 ring-white/12 hover:bg-white/[0.06] transition-colors duration-200">
                <Link2 className="w-3.5 h-3.5" /> Copy link
              </button>
              <button className="px-3 py-1.5 rounded-full text-[11px] flex items-center gap-1.5 text-white/65 ring-1 ring-white/12 hover:bg-white/[0.06] transition-colors duration-200">
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );

  const QuickActions = (
    <div className={`grid ${mobile ? "grid-cols-2 gap-3" : "grid-cols-4 gap-4"}`} data-testid="admin-quick-actions">
      {[
        { icon: Link2, label: "Bulk invite links", hint: "Paste numbers → personal links" },
        { icon: Megaphone, label: "WhatsApp follow-up", hint: "Segment by watch time" },
        { icon: BarChart3, label: "Polls & questions", hint: "Reusable question bank" },
        { icon: Circle, label: "Recordings", hint: "12 clips · cloud MP4" },
      ].map((a) => (
        <button
          key={a.label}
          className="text-left rounded-[22px] bg-white/[0.03] ring-1 ring-white/[0.07] p-4 hover:bg-white/[0.06] transition-colors duration-200"
        >
          <span className="w-9 h-9 grid place-items-center rounded-full bg-emerald-500/12 ring-1 ring-emerald-400/25 text-emerald-300 mb-3">
            <a.icon className="w-4 h-4" strokeWidth={1.7} />
          </span>
          <div className="text-[12.5px] text-white/90">{a.label}</div>
          <div className="text-[10.5px] text-white/35 mt-0.5">{a.hint}</div>
        </button>
      ))}
    </div>
  );

  const BannerPicker = (
    <div className="rounded-[24px] bg-white/[0.03] ring-1 ring-white/[0.07] p-5" data-testid="banner-picker">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80">Join screen banner</div>
          <div className="text-[11.5px] text-white/40 mt-1">This is the image every student sees before joining</div>
        </div>
        <button className="px-3.5 py-2 rounded-full text-[11px] flex items-center gap-1.5 text-white/70 ring-1 ring-white/12 hover:bg-white/[0.06] transition-colors duration-200">
          <Upload className="w-3.5 h-3.5" /> Upload your own
        </button>
      </div>
      <div className={`grid ${mobile ? "grid-cols-3 gap-2.5" : "grid-cols-5 gap-3"}`}>
        {BANNERS.map((b) => {
          const active = b.id === bannerId;
          return (
            <button
              key={b.id}
              data-testid={`banner-option-${b.id}`}
              onClick={() => onBanner(b.id)}
              className={`relative rounded-2xl overflow-hidden aspect-[16/9] transition-transform duration-200 hover:-translate-y-0.5 ${
                active ? "ring-2 ring-emerald-400" : "ring-1 ring-white/10"
              }`}
            >
              <img src={b.url} alt={b.label} className="w-full h-full object-cover" />
              <span className="absolute inset-x-0 bottom-0 px-2 py-1 bg-black/65 text-[9px] text-white/85 truncate">
                {b.label}
              </span>
              {active && (
                <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-emerald-400 grid place-items-center">
                  <Check className="w-3 h-3 text-black" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  const Insights = (
    <div className="w-[320px] shrink-0 space-y-4" data-testid="admin-insights">
      <div className="rounded-[24px] bg-white/[0.03] ring-1 ring-white/[0.07] p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80">Live class health</div>
          <span className="flex items-center gap-1.5 text-[10px] text-emerald-300">
            <Circle className="w-[7px] h-[7px] fill-emerald-400 text-emerald-400" /> LIVE
          </span>
        </div>
        <div className="flex items-end gap-1.5 h-20">
          {[38, 52, 61, 74, 88, 96, 92, 85, 79, 83, 90, 94].map((h, i) => (
            <span
              key={i}
              className="flex-1 rounded-t bg-gradient-to-t from-emerald-600/40 to-emerald-400"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between text-[10.5px] text-white/40">
          <span>Attention 91%</span>
          <span>Drop-off 6%</span>
        </div>
      </div>

      <div className="rounded-[24px] bg-white/[0.03] ring-1 ring-white/[0.07] p-5 space-y-3">
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/35">WhatsApp segments</div>
        {[
          ["Watched 45m+", "128", "emerald"],
          ["Watched 10–45m", "41", "amber"],
          ["Joined & left <10m", "15", "red"],
        ].map(([label, n, tone]) => (
          <div key={label} className="flex items-center justify-between">
            <span className="text-[12px] text-white/75">{label}</span>
            <span
              className={`px-2.5 py-0.5 rounded-full text-[11px] ${
                tone === "emerald"
                  ? "bg-emerald-400 text-black font-semibold"
                  : tone === "amber"
                  ? "bg-amber-400 text-black font-semibold"
                  : "bg-red-400 text-black font-semibold"
              }`}
            >
              {n}
            </span>
          </div>
        ))}
        <button className="w-full mt-1 py-2.5 rounded-full bg-emerald-500/12 ring-1 ring-emerald-400/30 text-[11px] uppercase tracking-[0.16em] text-emerald-300 hover:bg-emerald-500/20 transition-colors duration-200">
          Send follow-up
        </button>
      </div>

      <div className="rounded-[24px] bg-white/[0.03] ring-1 ring-white/[0.07] p-5 space-y-3">
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/35">Class defaults</div>
        {[
          ["Waiting room", true],
          ["Ask WhatsApp on join", true],
          ["Auto cloud recording", true],
          ["Students can unmute", false],
        ].map(([label, on]) => (
          <div key={label} className="flex items-center justify-between">
            <span className="text-[12px] text-white/70">{label}</span>
            <span
              className={`w-9 h-5 rounded-full relative transition-colors duration-200 ${
                on ? "bg-emerald-500" : "bg-white/[0.14] ring-1 ring-white/10"
              }`}
            >
              <span
                className="absolute top-0.5 w-4 h-4 rounded-full bg-black/85"
                style={{ left: on ? 18 : 2 }}
              />
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  if (mobile) {
    return (
      <div className="relative w-full h-full bg-[#070908] overflow-hidden flex flex-col">
        <Ambient />
        <div className="relative z-10 flex-1 p-5 pt-10 space-y-4 overflow-hidden">
          <div className="flex items-center justify-between">
            <UdyogLogo size={18} />
            <button className="w-10 h-10 grid place-items-center rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 text-black">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="font-display text-white text-2xl tracking-tight">My classes</div>
          {Stats}
          {QuickActions}
          {BannerPicker}
          {Rows}
        </div>
        <div className="relative z-10 mx-4 mb-4 flex items-center justify-around py-3 rounded-full bg-[#0b0d0c]/75 backdrop-blur-2xl ring-1 ring-white/[0.08]">
          {[LayoutDashboard, Video, Megaphone, BarChart3, Settings].map((Icon, i) => (
            <button
              key={i}
              className={`w-11 h-11 grid place-items-center rounded-full ${
                i === 0 ? "bg-emerald-500/18 text-emerald-300 ring-1 ring-emerald-400/35" : "text-white/45"
              }`}
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-[#070908] overflow-hidden flex">
      <Ambient />
      <aside className="relative z-10 w-56 shrink-0 p-6 flex flex-col gap-7">
        <UdyogLogo size={24} />
        <nav className="space-y-1">
          {[
            ["Dashboard", LayoutDashboard],
            ["Classes", Video],
            ["Attendance", Users],
            ["Recordings", Circle],
            ["WhatsApp", Megaphone],
            ["Analytics", BarChart3],
            ["Branding", Sparkles],
            ["Settings", Settings],
          ].map(([label, Icon], i) => (
            <button
              key={label}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-full text-[12.5px] transition-colors duration-200 ${
                i === 0
                  ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30"
                  : "text-white/45 hover:text-white/85 hover:bg-white/[0.04]"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.7} /> {label}
            </button>
          ))}
        </nav>
        <div className="mt-auto rounded-[22px] bg-white/[0.03] ring-1 ring-white/[0.07] p-4">
          <div className="text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80 mb-1.5">Plan</div>
          <div className="text-[12px] text-white/70">300 seats · unlimited classes</div>
        </div>
      </aside>

      <main className="relative z-10 flex-1 min-w-0 p-8 pl-2 space-y-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.28em] text-emerald-400/80">Teaching console</div>
            <h1 className="font-display text-white text-4xl tracking-tight mt-1.5">Good evening, Nikhil</h1>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-2 px-4 py-3 w-56 rounded-full bg-white/[0.04] ring-1 ring-white/[0.09]">
              <Search className="w-3.5 h-3.5 text-white/35" />
              <span className="text-[12px] text-white/30">Search classes…</span>
            </div>
            <button className="px-4 py-3 rounded-full ring-1 ring-white/12 text-white/70 text-[12px] flex items-center gap-2 hover:bg-white/[0.06] transition-colors duration-200">
              <Download className="w-4 h-4" /> Export
            </button>
            <button className="px-5 py-3 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 text-black text-[12px] font-semibold uppercase tracking-[0.16em] flex items-center gap-2 shadow-[0_12px_34px_-12px_rgba(16,185,129,0.9)]">
              <Plus className="w-4 h-4" /> New class
            </button>
          </div>
        </div>
        {Stats}
        <div className="flex gap-6">
          <div className="flex-1 min-w-0 space-y-5">
            {QuickActions}
            {BannerPicker}
            {Rows}
          </div>
          {Insights}
        </div>
      </main>
    </div>
  );
}
